import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp } = useAuth();
  const planId = location.state?.planId;
  const premiumIntent = location.state?.intent === 'premium';

  const [mode, setMode] = useState(location.state?.mode || 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmNotice, setConfirmNotice] = useState(false);

  const afterAuth = async () => {
    if (planId) {
      try {
        await api.post('/claim-plan', { plan_id: planId });
        toast.success('Your plan has been saved to your account');
      } catch (e) {
        console.error('Claim plan failed:', e);
      }
    }
    navigate(premiumIntent ? '/dashboard?premium=1' : '/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        const { data, error: err } = await signUp(email, password);
        if (err) throw err;
        if (!data.session) {
          setConfirmNotice(true);
          return;
        }
      } else {
        const { error: err } = await signIn(email, password);
        if (err) throw err;
      }
      await afterAuth();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E5EB] py-4">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <h1 className="text-2xl font-black tracking-tighter cursor-pointer" onClick={() => navigate('/')} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Clarity
          </h1>
        </div>
      </nav>

      <section className="py-16 sm:py-24">
        <div className="max-w-md mx-auto w-full px-6 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {confirmNotice ? (
              <div data-testid="confirm-email-notice" className="bg-[#F6F6F9] border border-[#E5E5EB] rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-bold text-[#0A0A0B] mb-3">Check your email</h2>
                <p className="text-base text-[#5C5C66] leading-relaxed mb-6">
                  We sent a confirmation link to <span className="font-semibold text-[#0A0A0B]">{email}</span>. Click it, then come back and log in.
                </p>
                <button
                  data-testid="go-to-login-btn"
                  onClick={() => { setConfirmNotice(false); setMode('login'); }}
                  className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-6 py-3 font-semibold"
                >
                  Go to Log In
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0B] mb-3 text-center">
                  {mode === 'signup' ? 'Create your account' : 'Welcome back'}
                </h2>
                <p className="text-base text-[#5C5C66] mb-10 text-center">
                  {planId
                    ? 'Save your plan and track your progress.'
                    : mode === 'signup'
                      ? 'Track your plan, chat with Clarity, and grow your score.'
                      : 'Log in to your Clarity dashboard.'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div data-testid="auth-error" className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                      {error}
                    </div>
                  )}
                  <div>
                    <label className="block mb-2 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">Email</label>
                    <input
                      data-testid="auth-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@email.com"
                      className="w-full bg-white border border-[#E5E5EB] rounded-xl px-5 py-4 text-lg font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all placeholder:text-[#A1A1AA]"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">Password</label>
                    <input
                      data-testid="auth-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Min. 6 characters"
                      className="w-full bg-white border border-[#E5E5EB] rounded-xl px-5 py-4 text-lg font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all placeholder:text-[#A1A1AA]"
                    />
                  </div>
                  <button
                    data-testid="auth-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#534AB7] text-white hover:bg-[#423B99] hover:-translate-y-0.5 transition-transform duration-200 rounded-xl px-8 py-4 font-semibold text-lg disabled:opacity-50"
                  >
                    {submitting ? 'Please wait...' : mode === 'signup' ? 'Sign Up Free' : 'Log In'}
                  </button>
                </form>

                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-[#E5E5EB]"></div>
                  <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">or</span>
                  <div className="flex-1 h-px bg-[#E5E5EB]"></div>
                </div>

                <button
                  data-testid="google-coming-soon-btn"
                  disabled
                  className="w-full bg-white text-[#A1A1AA] border border-[#E5E5EB] rounded-xl px-8 py-4 font-semibold text-base flex items-center justify-center gap-2 cursor-not-allowed"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#A1A1AA" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#A1A1AA" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#A1A1AA" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#A1A1AA" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Continue with Google — coming soon
                </button>

                <p className="text-center text-sm text-[#5C5C66] mt-8">
                  {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <button
                    data-testid="auth-toggle-btn"
                    onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); setError(null); }}
                    className="text-[#534AB7] font-semibold hover:underline"
                  >
                    {mode === 'signup' ? 'Log in' : 'Sign up'}
                  </button>
                </p>
              </>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AuthPage;
